/** One sampled point of a portfolio value series. */
export type T_PortfolioChartPoint = {
  timestamp: number;
  value: number;
};

export type T_PortfolioScrubDisplay = {
  dollarChange: number;
  isPositive: boolean;
  label: string;
  percentChange: number;
  value: number;
};

/** "Jul 14, 9:32 AM" — with the year appended once it differs from today. */
export function formatPortfolioScrubTimestamp(
  timestamp: number,
  now = new Date(),
): string {
  const date = new Date(timestamp);
  const datePart = date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Header substitution while the user holds and scans the portfolio chart:
 * the exact value at the scrubbed point plus its change from the start of the
 * visible window, labeled with the point's date & time.
 */
export function getPortfolioScrubDisplay(args: {
  chartPoints: T_PortfolioChartPoint[];
  now?: Date;
  scrubPoint: T_PortfolioChartPoint | null;
}): T_PortfolioScrubDisplay | null {
  const { chartPoints, scrubPoint } = args;
  if (!scrubPoint) return null;

  const baseline = chartPoints[0];
  const dollarChange = baseline ? scrubPoint.value - baseline.value : 0;
  const percentChange =
    baseline && baseline.value > 0 ? (dollarChange / baseline.value) * 100 : 0;

  return {
    dollarChange,
    isPositive: dollarChange >= 0,
    label: formatPortfolioScrubTimestamp(scrubPoint.timestamp, args.now),
    percentChange,
    value: scrubPoint.value,
  };
}
